import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import CommonService from '../../services/common.service';
import { API_URLS } from '../../constants/api-urls';
import './ProductRejected.scss';

export default function ProductRejected() {
  const { auctionId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [auction, setAuction] = useState(null);
  const [payment, setPayment] = useState(null);

  useEffect(() => {
    fetchData();
  }, [auctionId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [auctionRes, paymentRes] = await Promise.all([
        CommonService.get(`${API_URLS.AUCTION.BASE}/${auctionId}`).catch(() => ({ success: false })),
        CommonService.get(API_URLS.AUCTION.GET_PAYMENT_BY_AUCTION(auctionId)).catch(() => ({ success: false }))
      ]);
      if (auctionRes && auctionRes.success) setAuction(auctionRes.data);
      if (paymentRes && paymentRes.success) setPayment(paymentRes.data);
    } catch (e) {
      console.error('ProductRejected fetch:', e);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount == null || isNaN(amount)) return '₹0';
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
  };

  const totalPaid = payment?.total_amount ?? payment?.winning_bid_amount ?? 0;
  const handlingPercent = 3;
  const handlingAmount = totalPaid ? (totalPaid * handlingPercent) / 100 : 0;
  const refundAmount = totalPaid ? totalPaid - handlingAmount : 0;

  if (loading) {
    return (
      <div className="product-rejected-page">
        <div className="container">
          <div className="loading-state">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="product-rejected-page">
      <div className="container">
        <button type="button" className="back-btn" onClick={() => navigate(-1)}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Back
        </button>

        <div className="page-header">
          <div className="header-icon rejected">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <path d="M15 9L9 15M9 9L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
          <div>
            <h1>Product Rejected</h1>
            <p>Your rejection has been recorded and the seller has been notified.</p>
          </div>
        </div>

        <div className="info-banner">
          <h2>What happens next?</h2>
          <ul>
            <li>The seller has been notified of your rejection and the reason you provided.</li>
            <li>A <strong>refund will be processed to you</strong> for the amount you paid for this auction.</li>
            <li>A <strong>3% handling charge</strong> will be deducted from the refund amount.</li>
          </ul>
        </div>

        {totalPaid > 0 && (
          <div className="refund-card">
            <h3>Refund summary</h3>
            <div className="refund-row">
              <span className="label">Amount paid</span>
              <span className="value">{formatCurrency(totalPaid)}</span>
            </div>
            <div className="refund-row deduction">
              <span className="label">Handling charge (3%)</span>
              <span className="value">− {formatCurrency(handlingAmount)}</span>
            </div>
            <div className="refund-row total">
              <span className="label">Amount to be credited to you</span>
              <span className="value">{formatCurrency(refundAmount)}</span>
            </div>
            <p className="refund-note">Refund will be credited to your original payment method within 5–7 business days.</p>
          </div>
        )}

        {!totalPaid && (
          <p className="refund-note standalone">Refund details will be communicated via email. A 3% handling charge applies.</p>
        )}

        <div className="actions">
          <button type="button" className="btn-primary" onClick={() => navigate('/auction/my/bids')}>
            Back to My Bids
          </button>
        </div>
      </div>
    </div>
  );
}
